import { Box, Card, CardActions, CardContent, CardProps, Grid, Typography, TypographyProps } from '@mui/material';
import { isString } from 'lodash';
import React, { ReactNode } from 'react';
import { FlexBlock } from '../common';

type WidgetSizes = 'small' | 'medium' | 'large';

const sizes: Record<WidgetSizes, number> = {
  small: 250,
  medium: 400,
  large: 550
};

export const WidgetHeader = (props: TypographyProps) => <Typography variant="body2" fontWeight={600} {...props} />;

export interface WidgetLayoutProps extends CardProps {
  header: string | ReactNode;
  actions?: ReactNode;
  size?: WidgetSizes;
}

export const WidgetLayout = ({ header, children, actions, sx = {}, size, ...props }: WidgetLayoutProps) => (
  <Card
    sx={{
      height: size ? sizes[size] : 1,
      ...sx
    }}
    {...props}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box display="flex" flexDirection="column" gap={2} height={1}>
        {isString(header) ? (
          <WidgetHeader variant="body2" fontWeight={600}>
            {header}
          </WidgetHeader>
        ) : (
          header
        )}
        {children}
      </Box>
      {actions && (
        <>
          <FlexBlock />
          <CardActions sx={{ padding: 0, mt: 3 }}>{actions}</CardActions>
        </>
      )}
    </CardContent>
  </Card>
);

export interface DoubleWidgetLayoutProps extends Omit<CardProps, 'children'> {
  firstHeader: string | ReactNode;
  secondHeader: string | ReactNode;
  actions?: ReactNode;
  size?: WidgetSizes;
  children: [React.ReactNode, React.ReactNode];
}

export const DoubleWidgetLayout = ({
  firstHeader,
  secondHeader,
  children,
  actions,
  sx = {},
  size,
  ...props
}: DoubleWidgetLayoutProps) => (
  <Card
    sx={{
      height: size ? sizes[size] : 1,
      ...sx
    }}
    {...props}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid container spacing={2} alignItems="stretch" height="100%">
        <Grid item lg={6} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(firstHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {firstHeader}
            </WidgetHeader>
          ) : (
            firstHeader
          )}
          {children?.[0]}
        </Grid>
        <Grid item lg={6} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(secondHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {secondHeader}
            </WidgetHeader>
          ) : (
            secondHeader
          )}
          {children?.[1]}
        </Grid>
      </Grid>
      {actions && (
        <>
          <FlexBlock />
          <CardActions sx={{ padding: 0, mt: 3 }}>{actions}</CardActions>
        </>
      )}
    </CardContent>
  </Card>
);

export interface QuadWidgetLayoutProps extends Omit<CardProps, 'children'> {
  firstHeader: string | ReactNode;
  secondHeader: string | ReactNode;
  thirdHeader: string | ReactNode;
  fourthHeader: string | ReactNode;
  actions?: ReactNode;
  size?: WidgetSizes;
  children: [React.ReactNode, React.ReactNode, React.ReactNode, React.ReactNode];
}

export const QuadWidgetLayout = ({
  firstHeader,
  secondHeader,
  thirdHeader,
  fourthHeader,
  children,
  actions,
  sx = {},
  size,
  ...props
}: QuadWidgetLayoutProps) => (
  <Card
    sx={{
      height: size ? sizes[size] : 1,
      ...sx
    }}
    {...props}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid container spacing={2} alignItems="stretch" height="100%">
        <Grid item lg={3} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(firstHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {firstHeader}
            </WidgetHeader>
          ) : (
            firstHeader
          )}
          {children?.[0]}
        </Grid>
        <Grid item lg={3} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(secondHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {secondHeader}
            </WidgetHeader>
          ) : (
            secondHeader
          )}
          {children?.[1]}
        </Grid>
        <Grid item lg={3} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(thirdHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {thirdHeader}
            </WidgetHeader>
          ) : (
            thirdHeader
          )}
          {children?.[2]}
        </Grid>
        <Grid item lg={3} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(fourthHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {fourthHeader}
            </WidgetHeader>
          ) : (
            fourthHeader
          )}
          {children?.[3]}
        </Grid>
      </Grid>
      {actions && (
        <>
          <FlexBlock />
          <CardActions sx={{ padding: 0, mt: 3 }}>{actions}</CardActions>
        </>
      )}
    </CardContent>
  </Card>
);

export interface TrippleWidgetLayoutProps extends Omit<CardProps, 'children'> {
  firstHeader: string | ReactNode;
  secondHeader: string | ReactNode;
  thirdHeader: string | ReactNode;
  actions?: ReactNode;
  size?: WidgetSizes;
  children: [React.ReactNode, React.ReactNode, React.ReactNode];
}

export const TrippleWidgetLayout = ({
  firstHeader,
  secondHeader,
  thirdHeader,
  children,
  actions,
  sx = {},
  size,
  ...props
}: TrippleWidgetLayoutProps) => (
  <Card
    sx={{
      height: size ? sizes[size] : 1,
      ...sx
    }}
    {...props}
  >
    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid container spacing={2} alignItems="stretch" height="100%">
        <Grid item lg={4} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(firstHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {firstHeader}
            </WidgetHeader>
          ) : (
            firstHeader
          )}
          {children?.[0]}
        </Grid>
        <Grid item lg={4} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(secondHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {secondHeader}
            </WidgetHeader>
          ) : (
            secondHeader
          )}
          {children?.[1]}
        </Grid>
        <Grid item lg={4} display="flex" flexDirection="column" justifyContent="space-between">
          {isString(thirdHeader) ? (
            <WidgetHeader variant="body2" fontWeight={600}>
              {thirdHeader}
            </WidgetHeader>
          ) : (
            thirdHeader
          )}
          {children?.[2]}
        </Grid>
      </Grid>
      {actions && (
        <>
          <FlexBlock />
          <CardActions sx={{ padding: 0, mt: 3 }}>{actions}</CardActions>
        </>
      )}
    </CardContent>
  </Card>
);
